import type React from 'react';
import type { ExpandedLayout } from '../../config/constants';
import CrossIcon from '../CrossIcon';
import ExpandCenterIcon from '../icons/ExpandCenterIcon';
import PanelRightIcon from '../icons/PanelRightIcon';

interface PostConversionTopBarProps {
    onMinimize: () => void;
    viewLayout?: ExpandedLayout;
    onToggleLayout?: () => void;
    showLayoutToggle?: boolean;
    currentQuestion: number;
    totalFields: number;
    answeredFields: number;
}

const PostConversionTopBar: React.FC<PostConversionTopBarProps> = ({
    onMinimize,
    viewLayout = 'expanded',
    onToggleLayout,
    showLayoutToggle = false,
    currentQuestion,
    totalFields,
    answeredFields,
}) => {
    const progressPercent = totalFields > 0 ? (answeredFields / totalFields) * 100 : 0;

    return (
        <div
            className="inboundx-post-conversion-top-bar flex flex-col w-full"
            style={{ borderBottom: '1px solid var(--rw-border-subtle)' }}
        >
            <div className="flex justify-between items-center py-3 px-3 gap-1">
                {/* Left: layout toggle */}
                <div className="flex items-center gap-1">
                    {showLayoutToggle && onToggleLayout && (
                        <button
                            type="button"
                            onClick={onToggleLayout}
                            title={
                                viewLayout === 'expanded'
                                    ? 'Switch to side panel'
                                    : 'Switch to expanded view'
                            }
                            className="w-5 h-5 border-none bg-transparent cursor-pointer p-0 transition-all duration-200 ease-out hover:scale-110 hover:opacity-70"
                        >
                            {viewLayout === 'expanded' ? (
                                <PanelRightIcon
                                    width={20}
                                    height={20}
                                    color="var(--rw-icon-muted)"
                                />
                            ) : (
                                <ExpandCenterIcon
                                    width={20}
                                    height={20}
                                    color="var(--rw-icon-muted)"
                                />
                            )}
                        </button>
                    )}
                </div>

                {/* Center: progress bar + time estimate */}
                <div
                    className="flex items-center gap-2.5"
                    role="progressbar"
                    aria-label={`Question ${currentQuestion} of ${totalFields}`}
                    aria-valuenow={answeredFields}
                    aria-valuemin={0}
                    aria-valuemax={totalFields}
                >
                    <div className="relative w-[167px]">
                        <div
                            className="h-1.5 rounded-xl"
                            style={{ backgroundColor: 'var(--rw-border-subtle)' }}
                        />
                        <div
                            className="absolute top-0 left-0 h-1.5 rounded-xl"
                            style={{
                                width: `${progressPercent}%`,
                                backgroundColor: 'var(--rw-bg-action-primary)',
                                transition: 'width 0.4s ease-out',
                            }}
                        />
                    </div>
                    <span
                        className="whitespace-nowrap shrink-0"
                        style={{
                            fontSize: '16px',
                            lineHeight: '28px',
                            color: 'var(--rw-text-secondary)',
                            fontFamily: 'var(--rw-font-family)',
                            fontWeight: 500,
                        }}
                    >
                        {Math.round(progressPercent)}%
                    </span>
                </div>

                {/* Right: close button */}
                <button
                    type="button"
                    onClick={onMinimize}
                    title="Minimize chat"
                    className="inboundx-expanded-chat-minimize-button w-5 h-5 border-none bg-none cursor-pointer p-0 transition-all duration-200 ease-out hover:scale-110 hover:opacity-70"
                >
                    <CrossIcon width={20} height={20} color="var(--rw-icon-muted)" />
                </button>
            </div>
        </div>
    );
};

export default PostConversionTopBar;
